/**
 * LoginPage — Dual auth login with Okta SSO button and Cognito email/password form.
 * Detects @nepgroup.com emails and enforces Okta usage.
 */

import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { useAuth } from "../contexts/AuthContext";
import { isNepEmail, isOktaAvailable, isCognitoAvailable } from "../authMode";

export default function LoginPage() {
  const { isAuthenticated, isAuthenticating, login, loginWithOkta } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOktaHint, setShowOktaHint] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/";

  // Check for okta-required flag from session storage
  useEffect(() => {
    const oktaRequired = sessionStorage.getItem("okta-required");
    if (oktaRequired) {
      setError("NEP Group users must sign in with Okta SSO.");
      sessionStorage.removeItem("okta-required");
    }
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Detect @nepgroup.com email
  useEffect(() => {
    setShowOktaHint(isNepEmail(email));
  }, [email]);

  const handleCognitoLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isNepEmail(email)) {
      setError("NEP Group users must sign in with Okta SSO. Please use the 'Sign in with Okta' button.");
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || "Sign in failed. Please check your credentials.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAuthenticating) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
      <Card style={{ width: "100%", maxWidth: "420px" }}>
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <img
              src="/nep-logo.png"
              alt="NEP Group"
              className="mb-3"
              style={{ maxWidth: "160px", height: "auto" }}
            />
            <h2 className="fw-bold" style={{ color: "var(--accent, #0e67f5)" }}>AnyPlay</h2>
            <p className="text-muted mb-0">Multi-tenant streaming viewer</p>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Okta SSO Button */}
          {isOktaAvailable && (
            <>
              <Button
                variant="primary"
                className="w-100 mb-3 py-2"
                onClick={loginWithOkta}
                disabled={isSubmitting}
              >
                Sign in with Okta SSO
              </Button>

              {isCognitoAvailable && (
                <div className="text-center mb-3">
                  <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                    — or sign in with email —
                  </span>
                </div>
              )}
            </>
          )}

          {/* Cognito Email/Password Form */}
          {isCognitoAvailable && (
            <Form onSubmit={handleCognitoLogin}>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                {showOktaHint && (
                  <Form.Text className="text-warning">
                    @nepgroup.com accounts must use Okta SSO above.
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3" controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  disabled={showOktaHint}
                />
              </Form.Group>

              <Button
                variant="outline-primary"
                type="submit"
                className="w-100 py-2"
                disabled={isSubmitting || showOktaHint}
              >
                {isSubmitting ? (
                  <Spinner animation="border" size="sm" className="me-2" />
                ) : null}
                Sign In
              </Button>

              <div className="text-center mt-2">
                <small>
                  <a href="/forgot-password" className="text-decoration-none">Forgot Password?</a>
                </small>
              </div>
            </Form>
          )}

          <div className="text-center mt-4">
            <small className="text-muted">
              Internal NEP users: sign in with Okta.
              <br />
              External users: use your provided credentials.
            </small>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
}
